import React, { useEffect, useMemo, useState } from 'react';

import { Box, CircularProgress } from '@mui/material';
import { useUnit } from 'effector-react';

import { AuthorSelector } from './components/AuthorSelector';
import { StrategiesTable } from './components/StrategiesTable';
import { SummaryCards } from './components/SummaryCards';
import { WidgetCodeGenerator } from './components/WidgetCodeGenerator';
import { useUserById } from './queries';
import { StrategiesForList$ } from '../../effector/strategies';
import { User$ } from '../../effector/user';
import { useAllUsers } from '../../hooks/useUsers';
import { User } from '../../types/user';
import { UserRoles } from '../../types/userRoles';

export const Dashboard: React.FC = () => {
  const currentUser = useUnit(User$);
  const strategies = useUnit(StrategiesForList$);
  const { authors: allAuthors, isLoading: isLoadingAuthors } = useAllUsers();

  const [selectedAuthorId, setSelectedAuthorId] = useState<number | undefined>(undefined);

  const canSelectAuthor = useMemo(
    () => currentUser?.role === UserRoles.Administrator || currentUser?.role === UserRoles.DigitalExpert,
    [currentUser],
  );

  const { data: userData, isLoading: isLoadingUser } = useUserById(canSelectAuthor ? selectedAuthorId : undefined);

  const displayUser = useMemo((): User | null => {
    if (canSelectAuthor && selectedAuthorId) {
      return userData?.result || null;
    }
    return currentUser || null;
  }, [canSelectAuthor, selectedAuthorId, userData, currentUser]);

  const displayUserId = displayUser?.id;

  const userStrategies = useMemo(() => {
    if (!displayUserId) return [];
    return strategies.filter((s) => s.managerId === displayUserId);
  }, [strategies, displayUserId]);

  const authors = useMemo(() => {
    if (!canSelectAuthor) return [];

    return allAuthors.filter((u) => u.id !== currentUser?.id);
  }, [canSelectAuthor, allAuthors, currentUser]);

  const totalClients = useMemo(() => {
    if (!userStrategies.length) return 0;

    return userStrategies.reduce((acc, strategy) => acc + strategy.strategyAccounts, 0);
  }, [userStrategies]);

  const totalAssets = useMemo(() => {
    if (!userStrategies.length) return 0;

    return userStrategies.reduce((acc, strategy) => acc + strategy.strategyAccountPositions, 0);
  }, [userStrategies]);

  useEffect(() => {
    if (canSelectAuthor) {
      const department = authors.find((a) => a.name === 'Департамент автоследования');

      if (department) {
        setSelectedAuthorId(department.id);
      }
    }
  }, [authors, canSelectAuthor]);

  return (
    <>
      {(isLoadingUser || isLoadingAuthors) && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        {canSelectAuthor && (
          <AuthorSelector authors={authors} selectedAuthorId={selectedAuthorId} onAuthorChange={setSelectedAuthorId} />
        )}
      </Box>

      <SummaryCards
        strategiesCount={userStrategies.length}
        totalClients={totalClients}
        totalAssets={totalAssets}
        user={displayUser}
      />

      <StrategiesTable strategies={userStrategies} />

      <WidgetCodeGenerator strategies={userStrategies} />
    </>
  );
};
